import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { AiOutlineClear } from 'react-icons/ai';
import { BsSearch } from 'react-icons/bs';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { IoToday } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header/Header.tsx';
import Loading from '../components/loading/Loading.tsx';
import ConfirmModal from '../components/modal/ConfirmModal.tsx';
import Select from '../components/select/Select.tsx';
import { useLoadingContext } from '../login/context/LoadingContext.tsx';
import {
  Order,
  useProductContext,
} from '../product/context/ProductContext.tsx';
import { ProductOptionValues } from '../product/ProductType.ts';
import { useUserContext } from '../user/context/UserContext.tsx';
import {
  HOST,
  optionsStatus,
  ORDER_STATUS,
  ROUTE_NAME_OPTIONS,
} from '../utils/constants.ts';
import CartItemList from './cartItem/CartItemList.tsx';
import ExportOrdersButton from './ExportOrdersButton.tsx';
import ExportSummaryButton from './ExportProductsDayButton.tsx';
import './orders.scss';

const parseTimeSlot = (
  timeSlot: string | undefined | null
): { start: string; end: string } => {
  if (!timeSlot) {
    return { start: 'N/A', end: 'N/A' };
  }
  const [start, end] = timeSlot.split('-');
  return { start: start || 'N/A', end: end || 'N/A' };
};

const formatPriceWithDots = (price?: number) => {
  return Number(price || 0).toLocaleString('es-AR');
};

function Orders() {
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<ORDER_STATUS>();
  const [searchText, setSearchText] = useState<string>('');
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalOrders, setTotalOrders] = useState<Order[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [isShowingTodayOrdersAdmin, setIsShowingTodayOrdersAdmin] =
    useState<boolean>(false);

  const { userInfo } = useUserContext();

  const { showLoading, hideLoading, isLoading } = useLoadingContext();
  const isUser = !userInfo?.user?.isAdmin;
  const savedToken = localStorage.getItem('token');

  const previousPage = useRef(page);

  useEffect(() => {
    if (previousPage.current !== page) {
      if (!isUser) {
        searchOrders();
      }
      previousPage.current = page;
    }
  }, [page]);

  useEffect(() => {
    if (isUser) {
      getUserLatestOrders();
      setIsShowingTodayOrdersAdmin(false);
    } else {
      getAllOrdersFromThisDay();
      setIsShowingTodayOrdersAdmin(true);
    }
  }, [userInfo]);

  const getAllOrdersFromThisDay = async () => {
    try {
      const response = await fetch(`${HOST}/orders/admin-orders/today`, {
        headers: {
          Authorization: `Bearer ${savedToken}`,
        },
      });
      const orders = await response.json();
      setTotalOrders(orders);
      setFilteredOrders(orders);
    } catch (error) {
      throw error;
    }
  };

  const getUserLatestOrders = async () => {
    if (!userInfo?.user?.id) return;

    try {
      const response = await fetch(
        `${HOST}/orders/latest/${userInfo.user.id}`,
        {
          headers: {
            Authorization: `Bearer ${savedToken}`,
          },
        }
      );
      if (response.ok) {
        const orders = await response.json();
        setTotalOrders(orders);
        setFilteredOrders(orders);
        setLimit(isUser ? 10 : 50);
      } else {
        throw Error('error');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Hubo un problema al obtener los pedidos');
    }
  };

  const toggleAccordion = (orderId: string) => {
    setOpenOrderId((prevOrderId) => (prevOrderId === orderId ? null : orderId));
  };

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders((prevSelected) =>
      prevSelected.includes(orderId)
        ? prevSelected.filter((id) => id !== orderId)
        : [...prevSelected, orderId]
    );
  };

  const applyNewStatus = async () => {
    try {
      showLoading();
      const response = await fetch(`${HOST}/orders/update-multiple-statuses`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${savedToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderIds: selectedOrders, newStatus }),
      });
      if (response.ok) {
        toast.success('Estado actualizado correctamente');
        setShowModal(false);
        setSelectedOrders([]);
        getAllOrdersFromThisDay();
      } else {
        throw new Error('Error al actualizar el estado');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Hubo un problema al actualizar el estado');
    } finally {
      hideLoading();
    }
  };

  const calculateBoxesPerProduct = (orders: Order[]) => {
    const productBoxesMap: Record<
      string,
      {
        name: string;
        totalCrates: number;
        totalKg: number;
        totalGr: number;
        totalLt: number;
        totalPacks: number;
        totalUnits: number;
        totalMaples: number;
      }
    > = {};

    orders?.forEach((order) => {
      order.cart.items.forEach((item) => {
        const { product, quantity, purchaseOption } = item;

        // Valores por tipo
        let crateCount = 0;
        let kgCount = 0;
        let grCount = 0;
        let ltCount = 0;
        let packCount = 0;
        let unitCount = 0;
        let mapleCount = 0;

        switch (purchaseOption) {
          case ProductOptionValues.HALF_CRATE:
            crateCount = 0.5 * quantity;
            break;
          case ProductOptionValues.FULL_CRATE:
            crateCount = quantity;
            break;
          case ProductOptionValues.MAPLE:
            mapleCount = quantity;
            break;
          case ProductOptionValues.KG:
            kgCount = quantity;
            break;
          case ProductOptionValues.GR:
            grCount = quantity;
            break;
          case ProductOptionValues.LT:
            ltCount = quantity;
            break;
          case ProductOptionValues.PACK:
            packCount = quantity;
            break;
          case ProductOptionValues.UNIT:
            unitCount = quantity;
            break;
          default:
            break;
        }

        if (!productBoxesMap[product.id]) {
          productBoxesMap[product.id] = {
            name: product.name,
            totalCrates: 0,
            totalKg: 0,
            totalGr: 0,
            totalLt: 0,
            totalPacks: 0,
            totalUnits: 0,
            totalMaples: 0,
          };
        }

        productBoxesMap[product.id].totalCrates += crateCount;
        productBoxesMap[product.id].totalKg += kgCount;
        productBoxesMap[product.id].totalGr += grCount;
        productBoxesMap[product.id].totalLt += ltCount;
        productBoxesMap[product.id].totalPacks += packCount;
        productBoxesMap[product.id].totalUnits += unitCount;
        productBoxesMap[product.id].totalMaples += mapleCount;
      });
    });

    return Object.values(productBoxesMap);
  };

  const summary = calculateBoxesPerProduct(filteredOrders);
  const hasCrates = summary.some((item) => item.totalCrates > 0);
  const hasKg = summary.some((item) => item.totalKg > 0);
  const hasGr = summary.some((item) => item.totalGr > 0);
  const hasLt = summary.some((item) => item.totalLt > 0);
  const hasPacks = summary.some((item) => item.totalPacks > 0);
  const hasUnits = summary.some((item) => item.totalUnits > 0);
  const hasMaples = summary.some((item) => item.totalMaples > 0);

  function mapOrderStatus(status: string): string {
    switch (status) {
      case 'Pendiente':
        return ORDER_STATUS.PENDING;
      case 'Finalizado':
        return ORDER_STATUS.COMPLETE;
      case 'Cancelado':
        return ORDER_STATUS.CANCEL;
      default:
        return 'Estado desconocido';
    }
  }

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setFilterStatus(undefined);
    if (!isUser) {
      setSearchText('');
    }
  };

  const searchOrders = async () => {
    const params = new URLSearchParams();

    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (searchText) params.append('searchText', searchText);
    if (filterStatus) params.append('status', filterStatus);
    if (isUser && userInfo?.user.id) {
      params.append('userId', userInfo.user.id);
    }
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    try {
      showLoading();
      const response = await fetch(
        `${HOST}/orders/search?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${savedToken}`,
          },
        }
      );
      if (response.ok) {
        const { data, total } = await response.json();
        setFilteredOrders(data);
        setTotalOrders(total);
        setLimit(isUser ? 10 : 50);
      } else {
        throw new Error('Error al buscar pedidos');
      }
    } catch (error) {
      toast.error('Hubo un problema al buscar los pedidos');
    } finally {
      hideLoading();
    }
  };

  const getStatusClass = (status: ORDER_STATUS) => {
    switch (status) {
      case ORDER_STATUS.PENDING:
        return 'label-pending';
      case ORDER_STATUS.COMPLETE:
        return 'label-completed';
      case ORDER_STATUS.CANCEL:
        return 'label-canceled';
      default:
        return '';
    }
  };

  const handleCheckbox = (
    e: React.ChangeEvent<HTMLInputElement>,
    orderId: string
  ) => {
    e.stopPropagation();
    toggleOrderSelection(orderId);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders([]);
    } else {
      const allIds = filteredOrders.map((order) => order.id);
      setSelectedOrders(allIds);
    }
    setSelectAll(!selectAll);
  };

  const { setShoppingCart } = useProductContext();
  const navigate = useNavigate();

  const handleReorder = (orderId: string) => {
    const previousOrder = filteredOrders.find((o) => o.id === orderId);
    if (!previousOrder || !previousOrder.cart.items) return;

    const newCartItems = previousOrder.cart.items.map((item) => ({
      ...item,
      // totalPrice is recalculated later in ShoppingCart
      partialPrice: item.partialPrice,
    }));

    const newTotalPrice = newCartItems.reduce(
      (sum, item) => sum + item.partialPrice * item.quantity,
      0
    );

    const newTotalItems = newCartItems.reduce(
      (sum, item) => sum + item.quantity,
      0
    );

    setShoppingCart({
      items: newCartItems,
      totalPrice: newTotalPrice,
      totalItems: newTotalItems,
    });

    navigate(`/${ROUTE_NAME_OPTIONS.SHOPPING_CART}`);
  };

  return (
    <div>
      {showModal && (
        <ConfirmModal
          body={<ModalBody newStatus={newStatus} setNewStatus={setNewStatus} />}
          confirmText="Cambiar estado"
          action={() => applyNewStatus()}
          cancel={() => setShowModal(false)}
        />
      )}

      <Header />
      <div>
        <h3 className="product-title text-center">Mis Pedidos</h3>
        <div className="date-filter">
          <label className="flex-column">
            Desde
            <input
              className="input filters-width"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label className="flex-column">
            Hasta
            <input
              className="input filters-width"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          {!isUser && (
            <label className="flex-column">
              Por nombre
              <input
                className="input filters-width"
                placeholder="busca pedidos..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </label>
          )}
          <label className="width-150">
            Por estado
            <Select
              placeholder="estados"
              value={
                filterStatus
                  ? optionsStatus.find(
                      (option) => option.value === filterStatus
                    )
                  : null
              }
              options={optionsStatus}
              isSearchable={false}
              onChange={(selectedOption) =>
                setFilterStatus(selectedOption?.value || undefined)
              }
              styles={{
                input: (base) => ({
                  ...base,
                  pointerEvents: 'none',
                }),
                control: (base) => ({
                  ...base,
                  cursor: 'pointer',
                }),
              }}
            />
          </label>

          <div className="search-buttons-container">
            <button className="search-button primary" onClick={searchOrders}>
              <BsSearch className="icon" />
              Buscar
            </button>
            <button className="search-button secondary" onClick={clearFilters}>
              <AiOutlineClear className="icon" />
              Limpiar
            </button>
            {!isUser && (
              <button
                className="search-button secondary"
                onClick={getAllOrdersFromThisDay}
              >
                <IoToday className="icon" />
                Hoy
              </button>
            )}
          </div>
          {!isUser && (
            <>
              <button
                className="primary-button margin-top-21"
                onClick={() => setShowModal(true)}
                disabled={selectedOrders.length === 0}
              >
                Cambiar estado
              </button>
              <ExportOrdersButton filteredOrders={filteredOrders} />
            </>
          )}
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <div className="margin-top-21">
            {Array.isArray(filteredOrders) && filteredOrders.length > 0 ? (
              <div className="orders-summary-container">
                {!isUser && (
                  <div className="select-all-container">
                    <div>
                      <input
                        type="checkbox"
                        id="select-all"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        style={{ marginRight: '8px' }}
                      />
                      <label htmlFor="select-all">Seleccionar todos </label>
                    </div>
                    {totalOrders.length > 0 && (
                      <span style={{ fontWeight: 'bold' }}>
                        - Pedidos para hoy: {totalOrders.length}
                      </span>
                    )}
                  </div>
                )}
                {/* Lista de órdenes */}
                <div className="orders-list">
                  {filteredOrders.map((order: Order) => (
                    <div className="order-item" key={`order-${order.id}`}>
                      <div className="order-row">
                        <div
                          className={`order-accordion${openOrderId === order.id ? ' is-open' : ''}`}
                        >
                          <div className="div-flex">
                            <button
                              className={`accordion-header${openOrderId === order.id ? ' is-open' : ''}`}
                              onClick={() => toggleAccordion(order.id)}
                            >
                              {openOrderId === order.id ? (
                                <FaChevronDown className="margin-right" />
                              ) : (
                                <FaChevronUp className="margin-right" />
                              )}
                              <span className="accordion-header-text">
                                {order.user?.name} -{' '}
                                {new Date(order.date).toLocaleString([], {
                                  year: 'numeric',
                                  month: 'numeric',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            </button>
                            <span
                              className={`order-status-dot order-status-dot--${order.status?.toLowerCase().replace('é', 'e')}`}
                              title={order.status}
                            />
                            {!isUser && (
                              <input
                                className="checkbox-select-status"
                                type="checkbox"
                                checked={selectedOrders.includes(order.id)}
                                onChange={(e) => handleCheckbox(e, order.id)}
                              />
                            )}
                          </div>

                          {/* Contenido desplegable del acordeón */}
                          <div
                            className="accordion-content"
                            style={{
                              display:
                                openOrderId === order.id ? 'block' : 'none',
                            }}
                          >
                            <p>
                              <strong>Estado pedido:</strong>{' '}
                              <label
                                className={`label-status ${getStatusClass(
                                  order.status
                                )}`}
                              >
                                {mapOrderStatus(order.status)}
                              </label>
                            </p>
                            <p>
                              <strong>Fecha:</strong>{' '}
                              {new Date(order.date).toLocaleDateString()}
                            </p>
                            <p className="margin-bottom-5-negative">
                              <strong>Dirección:</strong>{' '}
                              {order.user?.address || 'N/A'}
                            </p>
                            <div className="flex margin-bottom-15-negative">
                              <p>
                                <strong>Entrega</strong>
                              </p>
                              <p className="margin-left-5">
                                <strong>desde:</strong>{' '}
                                {parseTimeSlot(order.timeSlot).start}
                              </p>
                              <p className="margin-left-5">
                                <strong>hasta:</strong>{' '}
                                {parseTimeSlot(order.timeSlot).end}
                              </p>
                            </div>

                            {/* Lista de productos */}
                            <CartItemList order={order} />

                            {/* Botón dentro del acordeón */}
                            <div className="reorder-button-container">
                              <div className="reorder-total">
                                <span className="reorder-total__label">
                                  Total:
                                </span>
                                <span className="reorder-total__value">
                                  ${formatPriceWithDots(order.totalPrice)}
                                </span>
                              </div>
                              <button
                                onClick={() => handleReorder(order.id)}
                                className="primary-button"
                              >
                                Volver a comprar
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Resumen admin */}
                {!isUser && (
                  <div className="orders-summary">
                    <table>
                      <thead>
                        <tr>
                          <th>Producto</th>
                          {hasCrates && <th>Cajones</th>}
                          {hasMaples && <th>Maples</th>}
                          {hasKg && <th>Kg</th>}
                          {hasGr && <th>Gramos</th>}
                          {hasLt && <th>Litros</th>}
                          {hasPacks && <th>Paquetes</th>}
                          {hasUnits && <th>Unidades</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {summary.map((item, index) => (
                          <tr key={`summary-${index}`}>
                            <td>{item.name}</td>
                            {hasCrates && <td>{item.totalCrates}</td>}
                            {hasMaples && <td>{item.totalMaples}</td>}
                            {hasKg && <td>{item.totalKg}</td>}
                            {hasGr && <td>{item.totalGr}</td>}
                            {hasLt && <td>{item.totalLt}</td>}
                            {hasPacks && <td>{item.totalPacks}</td>}
                            {hasUnits && <td>{item.totalUnits}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <ExportSummaryButton summary={summary} />
                  </div>
                )}
              </div>
            ) : (
              <p className="no-orders-message">No hay pedidos disponibles.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Orders;

const ModalBody = ({
  newStatus,
  setNewStatus,
}: {
  newStatus: string;
  setNewStatus: (status: string) => void;
}) => {
  return (
    <div>
      <h3>Cambiar estado de órdenes</h3>
      <Select
        isSearchable={false}
        placeholder="Eligue el nuevo estado"
        value={optionsStatus.find((option) => option.value === newStatus)}
        options={optionsStatus}
        onChange={(selectedOption) => setNewStatus(selectedOption?.value || '')}
      />
    </div>
  );
};
